import { timestampToISO, timestampToDateString } from './timestamp';

function str(value) {
    return value != null ? String(value) : '';
}

function parseJsonField(raw) {
    const text = str(raw).trim();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/** started_at → getStartedAt */
function protoTimestampGetterName(field) {
    return `get${field.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')}`;
}

/** Lê Timestamp do proto e grava RFC3339 UTC no objeto REST. */
function assignTimestampsFromProto(out, proto, fields) {
    const result = { ...out };
    if (!proto) {
        return result;
    }
    fields.forEach((field) => {
        const getter = protoTimestampGetterName(field);
        if (typeof proto[getter] !== 'function') {
            return;
        }
        const iso = timestampToISO(proto[getter]());
        if (iso) {
            result[field] = iso;
        }
    });
    return result;
}

export function paginationFromProto(meta) {
    if (!meta) {
        return {
            page: 1,
            limit: 10,
            total: 0,
            total_pages: 1,
        };
    }
    return {
        page: meta.getPage() || 1,
        limit: meta.getLimit() || 10,
        total: meta.getTotal() || 0,
        total_pages: meta.getTotalPages() || 1,
    };
}

/** List with top-level array + pagination (customers, printers, products, …). */
export function paginatedListResponse(listKey, items, meta) {
    return {
        [listKey]: items,
        ...paginationFromProto(meta),
    };
}

/** List nested under `data` (jobs, transactions, printer-maintenances). */
export function nestedListResponse(listKey, items, meta) {
    return {
        success: true,
        data: {
            [listKey]: items,
            ...paginationFromProto(meta),
        },
    };
}

export function customerRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {
        name: ref.getName() || '',
    };
    if (ref.getId()) {
        out.id = ref.getId();
        out.uuid = ref.getId();
    }
    if (ref.getPhone()) {
        out.phone = ref.getPhone();
    }
    if (ref.getEmail()) {
        out.email = ref.getEmail();
    }
    if (ref.getDocument()) {
        out.document = ref.getDocument();
    }
    return out;
}

export function salesChannelRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {};
    if (ref.getName()) {
        out.name = ref.getName();
    }
    if (typeof ref.getId === 'function' && ref.getId()) {
        out.id = ref.getId();
    }
    return Object.keys(out).length > 0 ? out : null;
}

export function shippingMethodRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {};
    if (ref.getName()) {
        out.name = ref.getName();
    }
    if (ref.getCost()) {
        out.cost = ref.getCost();
    }
    if (ref.getDeliveryDays()) {
        out.delivery_days = ref.getDeliveryDays();
    }
    if (ref.getDeadline()) {
        out.deadline = ref.getDeadline();
    }
    return out;
}

export function checkoutProductFromProto(p) {
    if (!p) {
        return null;
    }
    const id = p.getId() || '';
    const out = { quantity: p.getQuantity() || 0 };
    if (id) {
        out.id = id;
        out.uuid = id;
    }
    if (p.getName()) {
        out.name = p.getName();
    }
    if (p.getBasePrice()) {
        out.base_price = p.getBasePrice();
    }
    if (p.getCostPrice()) {
        out.cost_price = p.getCostPrice();
    }
    return out;
}

export function paymentMethodFromProto(p) {
    if (!p) {
        return null;
    }
    const out = {
        name: p.getName() || '',
        installments: p.getInstallments() || 0,
        amount: p.getAmount() || 0,
    };
    if (typeof p.getPaymentDate === 'function') {
        const paymentDate = timestampToISO(p.getPaymentDate());
        if (paymentDate) {
            out.payment_date = paymentDate;
        }
    }
    return out;
}

export function paymentDetailsFromProto(p) {
    if (!p) {
        return null;
    }
    const out = { paid: Boolean(p.getPaid()) };
    const paymentDate = timestampToISO(p.getPaymentDate());
    if (paymentDate) {
        out.payment_date = paymentDate;
    }
    return out;
}

export function userFromProto(u) {
    if (!u) {
        return null;
    }
    const out = {
        uuid: u.getUuid() || '',
        name: u.getName() || '',
        email: u.getEmail() || '',
    };
    if (u.getProfilePicture()) {
        out.profile_picture = u.getProfilePicture();
    }
    if (u.getWhatsapp()) {
        out.whatsapp = u.getWhatsapp();
    }
    if (u.getSite()) {
        out.site = u.getSite();
    }
    if (u.getInstagram()) {
        out.instagram = u.getInstagram();
    }
    if (u.getTiktok()) {
        out.tiktok = u.getTiktok();
    }
    if (u.getStoreName()) {
        out.store_name = u.getStoreName();
    }
    const createdAt = timestampToISO(u.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    const updatedAt = timestampToISO(u.getUpdatedAt());
    if (updatedAt) {
        out.updated_at = updatedAt;
    }
    return out;
}

export function loginResponseFromProto(res) {
    if (!res) {
        return {};
    }
    return authSessionResponseFromProto(res);
}

export function registerResponseFromProto(res) {
    const base = authSessionResponseFromProto(res);
    return {
        ...base,
        message: res?.getMessage?.() || '',
    };
}

export function authSessionResponseFromProto(res) {
    if (!res) {
        return {};
    }
    return {
        token_type: res.getTokenType() || 'Bearer',
        access_token: res.getAccessToken() || '',
        user: userFromProto(res.getUser()),
        plan: planInfoFromProto(res.getPlan?.()),
    };
}

export function planInfoFromProto(plan) {
    if (!plan) {
        return null;
    }
    return {
        name: plan.getName() || '',
        subscription_price: plan.getSubscriptionPrice() || 0,
    };
}

export function accountResponseFromProto(res) {
    return {
        data: {
            user: userFromProto(res?.getUser()),
            plan: planInfoFromProto(res?.getPlan()),
        },
    };
}

export function updateProfileResponseFromProto(res) {
    return {
        data: userFromProto(res?.getData()),
    };
}

export function updatePasswordResponseFromProto(res) {
    return {
        data: {
            updated: Boolean(res?.getUpdated()),
        },
    };
}

export function updateAvatarResponseFromProto(res) {
    return {
        data: {
            image_url: res?.getImageUrl() || '',
        },
    };
}

export function printerListItemFromProto(p) {
    if (!p) {
        return null;
    }
    return {
        id: p.getId() || '',
        name: p.getName() || '',
        brand: p.getBrand() || '',
        model: p.getModel() || '',
        consumption: p.getConsumption() || 0,
        price: p.getPrice() || 0,
        active: p.getActive(),
    };
}

export function printerFromProto(p) {
    if (!p) {
        return null;
    }
    const out = {
        uuid: p.getUuid() || '',
        name: p.getName() || '',
        brand: p.getBrand() || '',
        model: p.getModel() || '',
        power_consumption_watts: p.getPowerConsumptionWatts() || 0,
        acquisition_value: p.getAcquisitionValue() || 0,
        hourly_printing_cost: p.getHourlyPrintingCost() || 0,
        consumption: p.getPowerConsumptionWatts() || 0,
        price: p.getAcquisitionValue() || 0,
        active: p.getActive(),
    };
    const acquisitionDate = timestampToDateString(p.getAcquisitionDate());
    if (acquisitionDate) {
        out.acquisition_date = acquisitionDate;
    }
    return assignTimestampsFromProto(out, p, ['created_at', 'updated_at']);
}

export function filamentListItemFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        id: m.getId() || '',
        type: m.getType() || '',
        color: m.getColor() || '',
        color_label: m.getColorLabel() || '',
        brand: m.getBrand() || '',
        spool_weight: m.getSpoolWeight() || 0,
        current_weight: m.getCurrentWeight() || 0,
        cost: m.getCost() || 0,
        cost_per_kilogram: m.getCost() || 0,
    };
    const createdAt = timestampToISO(m.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function filamentFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        uuid: m.getUuid() || '',
        type: m.getType() || '',
        color: m.getColor() || '',
        color_label: m.getColorLabel() || '',
        brand: m.getBrand() || '',
        spool_weight: m.getSpoolWeight() || 0,
        current_weight: m.getCurrentWeight() || 0,
        cost: m.getCost() || 0,
    };
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

export function salesChannelFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        uuid: m.getUuid() || '',
        id: m.getUuid() || '',
        name: m.getName() || '',
        commission_percent: m.getCommissionPercent?.() || 0,
        fixed_fee: m.getFixedFee?.() || 0,
        payment_fee_percent: m.getPaymentFeePercent?.() || 0,
        min_fee: m.getMinFee?.() || 0,
        notes: m.getNotes?.() || '',
        dispatch_days: null,
    };
    if (typeof m.hasDispatchDays === 'function' && m.hasDispatchDays()) {
        out.dispatch_days = m.getDispatchDays();
    }
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

const PAYMENT_FEE_METHOD_LABELS = {
    credit: 'Crédito',
    debit: 'Débito',
    pix: 'PIX',
};

export function paymentFeeFromProto(m) {
    if (!m) {
        return null;
    }
    const method = m.getPaymentMethod() || '';
    const validTo = m.getValidTo() || '';
    const out = {
        uuid: m.getUuid() || '',
        id: m.getUuid() || '',
        payment_method: method,
        payment_method_label: PAYMENT_FEE_METHOD_LABELS[method] || method,
        fee_percent: m.getFeePercent?.() || 0,
        valid_from: m.getValidFrom() || '',
        valid_to: validTo || null,
        notes: m.getNotes?.() || '',
    };
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

export function productListItemFromProto(p) {
    if (!p) {
        return null;
    }
    const id = p.getId() || '';
    const out = {
        id,
        uuid: id,
        name: p.getName() || '',
        base_price: p.getBasePrice() || 0,
        stock_quantity: p.getStockQuantity() || 0,
        total_sales_quantity: p.getTotalSalesQuantity() || 0,
    };
    const createdAt = timestampToISO(p.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function productFromProto(p) {
    if (!p) {
        return null;
    }
    const availability = p.getAvailability();
    const measures = parseJsonField(p.getMeasures());
    const id = p.getId() || p.getUuid() || '';
    const out = {
        id,
        uuid: p.getUuid() || id,
        user_uuid: p.getUserUuid() || '',
        name: p.getName() || '',
        description: p.getDescription() || '',
        base_price: p.getBasePrice() || 0,
        cost_price: p.getCostPrice() || 0,
        stock_quantity: p.getStockQuantity() || 0,
        total_sales_quantity: p.getTotalSalesQuantity() || 0,
        is_customizable: Boolean(p.getIsCustomizable()),
        assembly_required: Boolean(p.getAssemblyRequired()),
        image: p.getImage() || '',
        images: p.getImagesList() || [],
    };
    if (measures != null) {
        out.measures = measures;
        out.measurements = measures;
    }
    if (availability) {
        out.availability = {
            lead_time_for_order: availability.getLeadTimeForOrder() || 0,
        };
    }
    return assignTimestampsFromProto(out, p, ['created_at', 'updated_at']);
}

export function customerListItemFromProto(c) {
    if (!c) {
        return null;
    }
    const id = c.getId() || '';
    const out = {
        id,
        uuid: id,
        name: c.getName() || '',
        phone: c.getPhone() || '',
        email: c.getEmail() || '',
        document: c.getDocument() || '',
    };
    const createdAt = timestampToISO(c.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function customerFromProto(c) {
    if (!c) {
        return null;
    }
    const doc = c.getDocument() || c.getDocumentNumber() || '';
    const out = {
        uuid: c.getUuid() || '',
        name: c.getName() || '',
        phone: c.getPhone() || '',
        email: c.getEmail() || '',
        document: doc,
        document_number: doc,
        address: c.getStreet() || '',
        address_number: c.getNumber() || '',
        address_complement: c.getComplement() || '',
        neighborhood: c.getNeighborhood() || '',
        city: c.getCity() || '',
        state: c.getState() || '',
        zip_code: c.getZipCode() || '',
    };
    return assignTimestampsFromProto(out, c, ['created_at', 'updated_at']);
}

function checkoutLikeFromProto(c, extra = {}) {
    if (!c) {
        return null;
    }
    const paymentMethods = typeof c.getPaymentMethodsList === 'function'
        ? c.getPaymentMethodsList()
        : [];
    const out = {
        id: c.getId() || '',
        customer: customerRefFromProto(c.getCustomer()),
        shipping_method: shippingMethodRefFromProto(c.getShippingMethod()),
        products: (c.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        amount: c.getAmount() || 0,
        sales_channel: salesChannelRefFromProto(c.getSalesChannel()),
        payment_methods: (paymentMethods || []).map(paymentMethodFromProto).filter(Boolean),
        payment_details: paymentDetailsFromProto(c.getPaymentDetails()),
        photo: c.getPhoto() || '',
        photo_url: c.getPhotoUrl() || '',
        ...extra,
    };
    if (typeof c.getExpectedNet === 'function') {
        out.expected_net = c.getExpectedNet() || 0;
    }
    if (typeof c.getBankReconciled === 'function') {
        out.bank_reconciled = Boolean(c.getBankReconciled());
    }
    if (typeof c.getFinishOrder === 'function') {
        out.finish_order = Boolean(c.getFinishOrder());
    }
    if (typeof c.getPaymentReceived === 'function') {
        out.payment_received = Boolean(c.getPaymentReceived());
    }
    if (typeof c.getIsQuickCheckout === 'function') {
        out.is_quick_checkout = Boolean(c.getIsQuickCheckout());
    }
    if (typeof c.getUseQuickCheckoutCreationDate === 'function') {
        out.use_quick_checkout_creation_date = Boolean(c.getUseQuickCheckoutCreationDate());
    }
    return assignTimestampsFromProto(out, c, ['created_at', 'updated_at']);
}

export function checkoutListItemFromProto(c) {
    if (!c) {
        return null;
    }
    return {
        id: c.getId() || '',
        customer: customerRefFromProto(c.getCustomer()),
        products: (c.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        sales_channel: salesChannelRefFromProto(c.getSalesChannel()),
        amount: c.getAmount() || 0,
        photo: c.getPhoto() || '',
        photo_url: c.getPhotoUrl() || '',
        created_at: timestampToISO(c.getCreatedAt()) || undefined,
    };
}

export function checkoutFromProto(c) {
    return checkoutLikeFromProto(c);
}

export function orderTopProductFromProto(p) {
    if (!p) {
        return null;
    }
    return {
        name: p.getName() || '',
        quantity: p.getQuantity() || 0,
    };
}

export function productSummaryFromProto(res) {
    if (!res) {
        return {
            total_products: 0,
            min_price: 0,
            max_price: 0,
            average_price: 0,
            total_sales_quantity: 0,
            total_stock_quantity: 0,
        };
    }
    return {
        total_products: res.getTotalProducts() || 0,
        min_price: res.getMinPrice() || 0,
        max_price: res.getMaxPrice() || 0,
        average_price: res.getAveragePrice() || 0,
        total_sales_quantity: res.getTotalSalesQuantity() || 0,
        total_stock_quantity: res.getTotalStockQuantity() || 0,
    };
}

export function orderSummaryFromProto(res) {
    if (!res) {
        return {
            total_sales: 0,
            total_products: 0,
            average_ticket: 0,
            top_products: [],
            from: '',
            to: '',
        };
    }
    return {
        total_sales: res.getTotalSales() || 0,
        total_products: res.getTotalProducts() || 0,
        average_ticket: res.getAverageTicket() || 0,
        top_products: (res.getTopProductsList() || []).map(orderTopProductFromProto).filter(Boolean),
        from: res.getFrom() || '',
        to: res.getTo() || '',
    };
}

export function orderListItemFromProto(o) {
    if (!o) {
        return null;
    }
    const out = {
        id: o.getId() || '',
        customer: customerRefFromProto(o.getCustomer()),
        products: (o.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        sales_channel: salesChannelRefFromProto(o.getSalesChannel()),
        shipping_method: shippingMethodRefFromProto(o.getShippingMethod()),
        amount: o.getAmount() || 0,
        expected_net: typeof o.getExpectedNet === 'function' ? (o.getExpectedNet() || 0) : (o.getAmount() || 0),
        payment_methods: (o.getPaymentMethodsList() || []).map(paymentMethodFromProto).filter(Boolean),
        payment_details: paymentDetailsFromProto(o.getPaymentDetails()),
        payment_received: Boolean(o.getPaymentReceived()),
        bank_reconciled: typeof o.getBankReconciled === 'function' ? Boolean(o.getBankReconciled()) : false,
    };
    const createdAt = timestampToISO(o.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function orderFromProto(o) {
    return checkoutLikeFromProto(o);
}

export function transactionFromProto(t) {
    if (!t) {
        return null;
    }
    const out = {
        id: t.getId() || '',
        type: t.getType() || '',
        amount: t.getAmount() || 0,
        deduction: t.getDeduction() || 0,
        net_amount: t.getNetAmount() || 0,
        description: t.getDescription() || '',
        payment_method: t.getPaymentMethod() || '',
        order_id: t.getOrderId() || '',
        authorization_id: typeof t.getAuthorizationId === 'function' ? (t.getAuthorizationId() || '') : '',
        source: typeof t.getSource === 'function' ? (t.getSource() || '') : '',
        outcome_category: typeof t.getOutcomeCategory === 'function' ? (t.getOutcomeCategory() || '') : '',
        bank_reconciled: typeof t.getAuthorizationId === 'function'
            ? Boolean(String(t.getAuthorizationId() || '').trim())
            : false,
    };
    return assignTimestampsFromProto(out, t, ['created_at', 'updated_at']);
}

export function transactionSummaryFromProto(t) {
    return transactionFromProto(t);
}

export function reconciliationFromProto(data) {
    if (!data) {
        return { success: true, data: {} };
    }
    return {
        success: true,
        data: {
            total_income: data.getTotalIncome() || 0,
            total_outcome: data.getTotalOutcome() || 0,
            balance: data.getBalance() || 0,
            transaction_count: data.getTransactionCount() || 0,
            income_count: data.getIncomeCount() || 0,
            outcome_count: data.getOutcomeCount() || 0,
            average_transaction: data.getAverageTransaction() || 0,
        },
    };
}

export function jobFilamentFromProto(f) {
    if (!f) {
        return null;
    }
    return { color: f.getColor() || '' };
}

export function jobFromProto(j) {
    if (!j) {
        return null;
    }
    const metadata = parseJsonField(j.getMetadataJson());
    const out = {
        id: j.getId() || j.getUuid() || '',
        uuid: j.getUuid() || j.getId() || '',
        printer_uuid: j.getPrinterUuid() || '',
        job_id: j.getJobId() || '',
        file_name: j.getFileName() || '',
        estimated_duration_minutes: j.getEstimatedDurationMinutes() || 0,
        estimated_filament_consumption_grams: j.getEstimatedFilamentConsumptionGrams() || 0,
        status: j.getStatus() || '',
        status_reason: j.getStatusReason() || '',
        object_count: j.getObjectCount() || 0,
        printed_count: j.getPrintedCount() || 0,
        success_percent: j.getSuccessPercent() || 0,
        filaments: (j.getFilamentsList() || []).map(jobFilamentFromProto).filter(Boolean),
    };
    if (metadata != null) {
        out.metadata = metadata;
    }
    return assignTimestampsFromProto(out, j, ['started_at', 'finished_at', 'created_at', 'updated_at']);
}

export function printerStatFromProto(s) {
    if (!s) {
        return null;
    }
    return {
        printer_uuid: s.getPrinterUuid() || '',
        total_jobs: s.getTotalJobs() || 0,
        total_objects: s.getTotalObjects() || 0,
        total_printed: s.getTotalPrinted() || 0,
        success_rate: s.getSuccessRate() || 0,
        total_duration_minutes: s.getTotalDurationMinutes() || 0,
        total_filament_grams: s.getTotalFilamentGrams() || 0,
    };
}

export function printerStatsResponseFromProto(res) {
    return {
        success: true,
        data: {
            printers: (res?.getPrintersList() || []).map(printerStatFromProto).filter(Boolean),
        },
    };
}

export function replacementPartFromProto(p) {
    if (!p) {
        return null;
    }
    return { name: p.getName() || '' };
}

export function printerMaintenanceFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        id: m.getId() || m.getUuid() || '',
        uuid: m.getUuid() || m.getId() || '',
        printer_uuid: m.getPrinterUuid() || '',
        type: m.getType() || '',
        problem_description: m.getProblemDescription() || '',
        analysis_duration_minutes: m.getAnalysisDurationMinutes() || 0,
        performed_actions: m.getPerformedActions() || '',
        printer_hours_count: m.getPrinterHoursCount() || 0,
        replacement_parts: (m.getReplacementPartsList() || []).map(replacementPartFromProto).filter(Boolean),
        correction_duration_minutes: m.getCorrectionDurationMinutes() || 0,
    };
    return assignTimestampsFromProto(out, m, ['occurred_at', 'created_at', 'updated_at']);
}

export function successResponseFromProto(res, fallbackMessage = '') {
    return {
        success: Boolean(res?.getSuccess?.() ?? true),
        message: res?.getMessage?.() || fallbackMessage,
    };
}

export function deleteResponseFromProto(res) {
    return {
        success: Boolean(res?.getSuccess?.() ?? true),
        message: res?.getMessage?.() || '',
    };
}
